import React, { useState } from 'react';
import Modal from '../Modal';
import { useToast } from '../Toast';
import { useUpsertVettingMutation } from '../../store/api/localApi';
import { VettingStatus, WritableVettingStatus } from '../../types';
import CredentialAttachmentInput from './CredentialAttachmentInput';

interface VettingFormProps {
  isOpen: boolean;
  onClose: () => void;
  staffUserId: string;
}

/**
 * Record a NEW Garda vetting disclosure. Always an ADD — there is no edit
 * mode, because vetting is append-only (see server: upsert-vetting always
 * writes a new row, never updates one). Admin-only; the server also refuses
 * a self-write regardless of what the UI offers, but the UI never renders
 * this form on a staff member's own profile in the first place.
 */
const VettingForm: React.FC<VettingFormProps> = ({ isOpen, onClose, staffUserId }) => {
  const { showSuccess, showError } = useToast();
  const [upsertVetting, { isLoading }] = useUpsertVettingMutation();

  const [vettingStatus, setVettingStatus] = useState<WritableVettingStatus>(
    VettingStatus.IN_PROGRESS
  );
  const [disclosureDate, setDisclosureDate] = useState('');
  const [nvbReference, setNvbReference] = useState('');
  const [vettedThrough, setVettedThrough] = useState('');
  const [decisionSatisfactory, setDecisionSatisfactory] = useState(false);
  const [revetDueAt, setRevetDueAt] = useState('');
  const [attachmentS3Key, setAttachmentS3Key] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);

  const resetForm = () => {
    setVettingStatus(VettingStatus.IN_PROGRESS);
    setDisclosureDate('');
    setNvbReference('');
    setVettedThrough('');
    setDecisionSatisfactory(false);
    setRevetDueAt('');
    setAttachmentS3Key(undefined);
    setIsUploading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await upsertVetting({
        staffUserId,
        vettingStatus,
        ...(disclosureDate ? { disclosureDate } : {}),
        ...(nvbReference.trim() ? { nvbReference: nvbReference.trim() } : {}),
        ...(vettedThrough.trim() ? { vettedThrough: vettedThrough.trim() } : {}),
        ...(decisionSatisfactory ? { decision: 'SATISFACTORY' as const } : {}),
        ...(revetDueAt ? { revetDueAt } : {}),
        ...(attachmentS3Key ? { attachmentS3Key } : {}),
      }).unwrap();
      showSuccess('Vetting record added');
      resetForm();
      onClose();
    } catch (err: any) {
      showError(err.data?.error || 'Failed to record vetting');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record Garda Vetting"
      testId="vetting-form-modal"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          This adds a new vetting record. A previous record, if any, is kept as history — recording
          a new one is how a re-vet is captured.
        </p>
        <div>
          <label htmlFor="vetting-status" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            id="vetting-status"
            data-testid="vetting-status-select"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={vettingStatus}
            onChange={(e) => setVettingStatus(e.target.value as WritableVettingStatus)}
          >
            <option value={VettingStatus.IN_PROGRESS}>In progress</option>
            <option value={VettingStatus.VETTED}>Vetted</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="vetting-disclosure-date"
            className="block text-sm font-medium text-gray-700"
          >
            Disclosure date
          </label>
          <input
            id="vetting-disclosure-date"
            type="date"
            data-testid="vetting-disclosure-date-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={disclosureDate}
            onChange={(e) => setDisclosureDate(e.target.value)}
          />
        </div>
        <div>
          <label
            htmlFor="vetting-nvb-reference"
            className="block text-sm font-medium text-gray-700"
          >
            NVB reference
          </label>
          <input
            id="vetting-nvb-reference"
            type="text"
            data-testid="vetting-nvb-reference-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={nvbReference}
            onChange={(e) => setNvbReference(e.target.value)}
          />
        </div>
        <div>
          <label
            htmlFor="vetting-vetted-through"
            className="block text-sm font-medium text-gray-700"
          >
            Vetted through
          </label>
          <input
            id="vetting-vetted-through"
            type="text"
            placeholder="e.g. Tusla, Coach Ireland"
            data-testid="vetting-vetted-through-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={vettedThrough}
            onChange={(e) => setVettedThrough(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="vetting-decision-satisfactory"
            type="checkbox"
            data-testid="vetting-decision-satisfactory-checkbox"
            checked={decisionSatisfactory}
            onChange={(e) => setDecisionSatisfactory(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="vetting-decision-satisfactory" className="text-sm text-gray-700">
            Decision: Satisfactory
          </label>
        </div>
        <div>
          <label htmlFor="vetting-revet-due" className="block text-sm font-medium text-gray-700">
            Re-vet due
          </label>
          <input
            id="vetting-revet-due"
            type="date"
            data-testid="vetting-revet-due-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={revetDueAt}
            onChange={(e) => setRevetDueAt(e.target.value)}
          />
        </div>
        <CredentialAttachmentInput
          staffUserId={staffUserId}
          onChange={setAttachmentS3Key}
          onUploadingChange={setIsUploading}
          inputTestId="vetting-attachment-input"
        />
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isUploading}
            data-testid="vetting-form-submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isUploading ? 'Uploading...' : isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default VettingForm;
